/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useQuery } from '@tanstack/react-query';

import { getMultipleSearchResults, getSearchResults } from './search-api';
import { getNextWeekend } from '../utils/getNextWeekend';
import { get2025HolidayStaysDates } from '../utils/get2025HolidayStaysDates';

export const CAMPSITES_AVAILABLE_THIS_WEEKEND_KEY = 'campsites-available-this-weekend';
export const TICKETS_AVAILABLE_THIS_WEEKEND_KEY = 'tickets-available-this-weekend';
export const PERMITS_NEARBY = 'permits-nearby';
export const VENUES_AVAILABLE_THIS_WEEKEND_KEY = 'permits-available-this-weekend';
export const CAMPSITES_NEAR_WATER_KEY = 'campsites-near-water';
export const HOLIDAY_STAYS_KEY = 'holiday-stays';

export const RECOMMENDATION_RADIUS = '2000';

export function useCampsitesAvailableThisWeekendQuery({
    isEnabled,
    location,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
}) {
    const [startDate, endDate] = getNextWeekend(); // Date objects in UTC time

    const searchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'available',
        size: '10',
        start: '0',
        radius: RECOMMENDATION_RADIUS,
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'campsite_type_of_use:Overnight',
    });

    // Using append to add multiple fq parameters
    searchParams.append('fq', 'campsite_type_of_use:na');
    searchParams.append('fq', 'entity_type:campground');

    if (location) {
        searchParams.append('location', location);
    }

    if (latitude && longitude) {
        searchParams.append('lat', latitude.toString());
        searchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [CAMPSITES_AVAILABLE_THIS_WEEKEND_KEY, searchParams.toString()],
        queryFn: async ({ signal }) => getSearchResults(searchParams, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

export function useTicketsAvailableThisWeekendQuery({
    isEnabled,
    location,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
}) {
    const [startDate, endDate] = getNextWeekend({ weekendStartDay: 'saturday' }); // Date objects in UTC time

    const searchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'available',
        size: '10',
        start: '0',
        radius: RECOMMENDATION_RADIUS,
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'entity_type:ticketfacility',
    });

    // Using append to add multiple fq parameters
    searchParams.append('fq', 'entity_type:timedentry');
    searchParams.append('fq', 'entity_type:activitypass');

    if (location) {
        searchParams.append('location', location);
    }

    if (latitude && longitude) {
        searchParams.append('lat', latitude.toString());
        searchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [TICKETS_AVAILABLE_THIS_WEEKEND_KEY, searchParams.toString()],
        queryFn: async ({ signal }) => getSearchResults(searchParams, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

export function usePermitsNearbyQuery({
    isEnabled,
    location,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
}) {
    const searchParams = new URLSearchParams({
        sort: 'score',
        size: '10',
        start: '0',
        radius: RECOMMENDATION_RADIUS,
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'entity_type:permit',
    });

    // Using append to add multiple fq parameters
    searchParams.append('fq', 'entity_type:vehiclepermit');
    searchParams.append('fq', 'campsite_type_of_use:na');

    if (location) {
        searchParams.append('location', location);
    }

    if (latitude && longitude) {
        searchParams.append('lat', latitude.toString());
        searchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [PERMITS_NEARBY, searchParams.toString()],
        queryFn: async ({ signal }) => getSearchResults(searchParams, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

export function useVenuesAvailableThisWeekendQuery({
    isEnabled,
    location,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
}) {
    const [startDate, endDate] = getNextWeekend({ weekendStartDay: 'saturday' }); // Date objects in UTC time

    const searchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'available',
        size: '10',
        start: '0',
        radius: '4000',
        include_partially_available: 'true',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'campsite_type_of_use:Day',
    });

    // Using append to add multiple fq parameters
    searchParams.append('fq', 'entity_type:campground');
    searchParams.append('fq', 'entity_type:venuereservations');
    searchParams.append('fq', 'campsite_type_of_use:na');

    if (location) {
        searchParams.append('location', location);
    }

    if (latitude && longitude) {
        searchParams.append('lat', latitude.toString());
        searchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [VENUES_AVAILABLE_THIS_WEEKEND_KEY, searchParams.toString()],
        queryFn: async ({ signal }) => getSearchResults(searchParams, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

export function useNearWaterQuery({
    isEnabled,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    latitude?: number;
    longitude?: number;
}) {
    const [startDate, endDate] = getNextWeekend(); // Date objects in UTC time

    const fqParams = [
        '-entity_type:(tour OR timedentry_tour)',
        'asset_activities:SWIMMING',
        'asset_activities:BOATING',
        'asset_activities:DIVING',
        'asset_activities:FISHING',
        'asset_activities:WATER SPORTS',
        'asset_activities:SNORKELING',
    ];

    const fgParams = ['boat', 'boat-in', 'camping', 'dayuse'];

    const searchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'score',
        size: '10',
        start: '0',
        radius: RECOMMENDATION_RADIUS,
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
    });

    fqParams.forEach((param) => searchParams.append('fq', param));
    fgParams.forEach((param) => searchParams.append('fg', param));

    if (latitude && longitude) {
        searchParams.append('lat', latitude.toString());
        searchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [CAMPSITES_NEAR_WATER_KEY, searchParams.toString()],
        queryFn: async ({ signal }) => getSearchResults(searchParams, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

export function useHolidayStaysQuery({
    isEnabled,
    location,
    latitude,
    longitude,
}: {
    isEnabled: boolean;
    location?: string;
    latitude?: number;
    longitude?: number;
}) {
    const [startDate, endDate] = get2025HolidayStaysDates();

    const lodgingSearchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'distance',
        size: '10',
        start: '0',
        radius: '8000',
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'campsite_type_of_use:Overnight',
        fg: 'lodging',
    });

    const campingSearchParams = new URLSearchParams({
        start_date: startDate.toISOString(), // 2025-05-09T00:00:00.000Z
        end_date: endDate.toISOString(), // 2025-05-11T00:00:00.000Z
        sort: 'distance',
        size: '10',
        start: '0',
        radius: RECOMMENDATION_RADIUS,
        include_partially_available: 'false',
        include_notreservable: 'false',
        include_unavailable: 'false',
        fq: 'campsite_type_of_use:Overnight',
    });

    campingSearchParams.append('fq', 'campsite_type_of_use:na');
    campingSearchParams.append('fq', 'entity_type:campground');

    if (location) {
        lodgingSearchParams.append('location', location);
        campingSearchParams.append('location', location);
    }

    if (latitude && longitude) {
        lodgingSearchParams.append('lat', latitude.toString());
        lodgingSearchParams.append('lng', longitude.toString());
        campingSearchParams.append('lat', latitude.toString());
        campingSearchParams.append('lng', longitude.toString());
    }

    return useQuery({
        queryKey: [HOLIDAY_STAYS_KEY, lodgingSearchParams.toString()],
        queryFn: async ({ signal }) =>
            getMultipleSearchResults({
                signal,
                requestParams: [lodgingSearchParams, campingSearchParams],
            }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        retry: 3,
        enabled: isEnabled,
    });
}

// cSpell:ignore include_notreservable, timedentry_tour, dayuse, ticketfacility, activitypass, vehiclepermit, timedentry, venuereservations
